'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  name: string;
  email: string;
  phone: string;
  department: string;
  subject: string;
  message: string;
}

export default function ContactForm() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    department: '',
    subject: '',
    message: '',
  });
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const departments = [
    { id: 'reservations', name: 'Reservations', icon: 'CalendarIcon' },
    { id: 'corporate', name: 'Corporate Partnerships', icon: 'BuildingOfficeIcon' },
    { id: 'guest-services', name: 'Guest Services', icon: 'UserGroupIcon' },
    { id: 'general', name: 'General Inquiries', icon: 'ChatBubbleLeftIcon' },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isHydrated) return;
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        department: '',
        subject: '',
        message: '',
      });
    }, 3000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  if (!isHydrated) {
    return (
      <section id="contact-form" className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-gray-200 rounded w-1/2 mx-auto"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto"></div>
              <div className="grid grid-cols-2 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-24 bg-gray-200 rounded"></div>
                ))}
              </div>
              <div className="h-32 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact-form" className="py-20 bg-white">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 text-accent mb-4">
              <Icon name="EnvelopeIcon" size={24} />
              <span className="text-sm font-semibold uppercase tracking-wide">Contact Form</span>
            </div>
            <h2 className="text-3xl lg:text-5xl font-display font-bold text-primary mb-4">
              Send Us a Message
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Fill out the form below and our team will get back to you within 24 hours. For urgent matters, please call us directly.
            </p>
          </div>

          {showSuccess && (
            <div className="mb-6 p-4 bg-success/10 border border-success rounded-lg flex items-center space-x-3">
              <Icon name="CheckCircleIcon" size={24} className="text-success" />
              <p className="text-success font-medium">Message sent successfully! We'll get back to you shortly.</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="bg-card rounded-xl shadow-subtle p-8 border border-border space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-300"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-300"
                  required
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 98765 43210"
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-300"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Subject *</label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Booking inquiry"
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-300"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-4">Department *</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {departments.map((dept) => (
                  <button
                    key={dept.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, department: dept.id })}
                    className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                      formData.department === dept.id
                        ? 'border-accent bg-accent/5' :'border-border hover:border-accent/50'
                    }`}
                  >
                    <Icon name={dept.icon} size={24} className="mx-auto mb-2 text-accent" />
                    <p className="text-xs font-medium text-text-primary text-center">{dept.name}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">Message *</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={6}
                placeholder="Please provide details about your inquiry..."
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-300 resize-none"
                required
              />
            </div>

            <button
              type="submit"
              disabled={!formData.name || !formData.email || !formData.department || !formData.subject || !formData.message}
              className="w-full bg-accent hover:bg-accent/90 text-white font-semibold py-4 px-6 rounded-lg shadow-subtle hover:shadow-elevated transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              <span>Send Message</span>
              <Icon name="PaperAirplaneIcon" size={20} />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}